import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { ApplicationActions } from '../../actions/application.actions';
import { Validation } from '../../shared/services/validation.service';
import { FlowManagerService } from '../../shared/services/flowmanager.service';
import * as _ from 'lodash';
import { NgRedux } from '@angular-redux/store';

@Component({
  selector: 'md-currency',
  template: require('./currency.component.html')
})
export class CurrencyComponent {
  @Input() options;
  @Input() showUp;
  @ViewChild('inputElement') editField;
  @Output() valueChange = new EventEmitter();

  constructor(elementRef: ElementRef,
    validation: Validation,
    flowManager: FlowManagerService,
    appActions: ApplicationActions,
    ngRedux: NgRedux) {
    this._elementRef = elementRef;
    this.appActions = appActions;
    this.validation = validation;
    this.flowManager = flowManager;
    this.isValid = true;
    this.init = false;
    this.lastGoodFormattedValue = '';
    this.validateSub = this.validation.validateInput.subscribe(event => this.triggerValidation());
    this.ngRedux = ngRedux;
    this.schema = this.flowManager.getValidationSchema();
  }

  ngOnInit() {
    if (this.options.attr.inputClass === undefined) {
      this.options.attr.inputClass = '';
    }

    this.inputClass = this.options.attr.inputClass;
  }

  ngAfterViewInit() {
    if (this.options.inputPath) {
      const arrayPath = this.options.inputPath.split('.');

      arrayPath.unshift('applicationStore');
      this.arrayPath = arrayPath;

      if (this.options.inputPath.match(/\[\d+\]/) !== -1) {
        this.reduxObservable$ = this.ngRedux.select(
          state => _.get(state, `applicationStore.${this.options.inputPath}`)
        );
      } else {
        this.reduxObservable$ = this.ngRedux.select(this.arrayPath);
      }

      // Set default value if it exists in schema and state is null
      this.defaultValue = this.options.attr.defaultValue || null;
      this.initialReduxValue = this.appActions.getData(this.options.inputPath);
      if (!this.initialReduxValue && this.defaultValue !== null && this.init === false) {
        this.appActions.setData(this.options.inputPath, this.defaultValue);
      }
      // Don't validate on change for liquid assets or initial investment
      const rules = this.schema[this.options.validationType || ''];
      if (!rules.validateOnSubmit) {
        this.reduxSubscription = this.reduxObservable$.subscribe(value => {
          const validatedValue = typeof value === 'undefined' ? '' : value;
          this.onChange(validatedValue);

          if ((this.currentValue !== null && this.currentValue !== '') || this.init === true) {
            this.triggerValidation();

            const payload = {
              id: this.options.attr.id,
              value: this.currentValue,
              isValid: this.isValid
            };

            this.valueChange.emit(payload);
          }

          this.init = true;
        });
      } else {
        // Make sure to populate the value from the store if you don't validation on change
        this.onChange(this.appActions.getData(this.options.inputPath) || '');
      }
    }
  }

  ngOnDestroy() {
    if (this.validateSub) {
      this.validateSub.unsubscribe();
    }

    if (this.options.inputPath) {
      this.reduxObservable$.unsubscribe();
      if (this.reduxSubscription) {
        this.reduxSubscription.unsubscribe();
      }
    }
  }

  /**
   * Format the numeric or string input value with commas and decimal places for display
   * @param  {number,string} value       Value to massage for display
   * @param  {int} cursorIndex If specified, returns the index where the cursor is for the input string
   *                           to allow it to be mapped to the proper output string location.
   * @return {object}             Formatted value and cursorIndex
   */
  _formatValue(value, cursorIndex) {
    let formattedValue = (typeof value === 'undefined' || value === null) ? '' : value;
    formattedValue = formattedValue.toString();
    let formattedCursorIndex = (typeof cursorIndex === 'undefined') ? value.length : cursorIndex;

    // Determine where the new cursor should go (indicated with _) and add commas to the new string
    if (this.currentValue > 1) {
      const formatterRegex = /(\d|\d_|_\d)(?=(?:(?:\d|\d_|_\d){3})+(?:\.(?:_|\d|\d_|_\d){0,2})?$)/g;
      formattedValue = (
        `${formattedValue.substring(0, cursorIndex)}_${formattedValue.substr(cursorIndex)}`
      ).replace(formatterRegex, '$1,');
      formattedCursorIndex = formattedValue.indexOf('_');
      formattedValue = formattedValue.replace('_', '');
    }

    return {
      value: formattedValue,
      cursorIndex: formattedCursorIndex
    };
  }

  /**
   * Processes the current value of the input field and attempts to format it properly
   */
  onChange(value) {
    const commaRegex = /,/g;
    const decimalRegex = !this.options.blockDecimals
      ? /^((?:0(?=[\d\.]))*(\d*\.?\d{0,2}))$/
      : /^((?:0(?=[\d\.]))*(\d*))$/;

    // Track field value before an after initial cursor position
    // Remove commas from the currently displayed value
    let cleanValue = value.replace(commaRegex, '');

    const beforeValue = value.substring(0, this.editField.nativeElement.selectionStart);
    const cleanBefore = beforeValue.replace(commaRegex, '');
    const numRemovedBefore = beforeValue.length - cleanBefore.length;

    let cursorIndex = this.editField.nativeElement.selectionStart - numRemovedBefore;

    if (cursorIndex < 0) {
      cursorIndex = 0;
    }

    if (cursorIndex > cleanValue.length) {
      cursorIndex = cleanValue.length;
    }

    // Make sure we pass the test for a valid decimal number
    if (!decimalRegex.test(cleanValue)) {
      // If the entire value is removed we want to keep it removed
      if (value === '') {
        this.lastGoodFormattedValue = '';
        this.formattedValue = '';
        this.currentValue = '';
        this.editField.nativeElement.value = '';
      } else {
        this.editField.nativeElement.value = this.lastGoodFormattedValue;
      }
      return false;
    }

    cleanValue = decimalRegex.exec(cleanValue)[1];

    // Parse the number string to get the real value and check min and max conditions
    if (cleanValue === '.') {
      cleanValue = '0.';
      cursorIndex++;
    }

    const parsedValue = parseFloat(cleanValue);
    if (isNaN(parsedValue)) {
      this.formattedValue = '';
      this.currentValue = '';
      this.editField.nativeElement.value = '';
      return false;
    }

    this.currentValue = parsedValue;
    this.appActions.setData(this.options.inputPath, parsedValue.toString());

    const formatted = this._formatValue(cleanValue, cursorIndex);
    this._populateField(formatted.value, formatted.cursorIndex);
    return true;
  }

  /**
   * Update the DOM input field with the new value and cursor position
   * @param  {string} value          Text to display
   * @param  {int} selectionStart Starting point of cursor position in input field
   * @param  {int} selectionEnd   Ending point of cursor position in input field
   */
  _populateField(value, selectionStart, selectionEnd) {
    this.formattedValue = value;
    this.lastGoodFormattedValue = value;
    if (typeof selectionStart !== 'undefined' && typeof selectionEnd !== 'undefined') {
      this.editField.nativeElement.setSelectionRange(selectionStart, selectionEnd);
    }
  }

  /**
  * Handles saving on Enter keydown, saves previous value of input field before the keystroke is persisted
  * @param  {KeyEvent} event DOM event
  */
  onKeyDown(event) {
    const ctrlKey = event.ctrlKey ? event.ctrlKey : false;
    const metaKey = event.metaKey ? event.metaKey : false;
    const shiftKey = event.shiftKey ? event.shiftKey : false;
    const tabKey = event.key === 'Tab';
    const allowDecimals = !this.options.blockDecimals && (event.keyCode === 110 || event.keyCode === 190);

    return ((event.keyCode >= 48 && event.keyCode <= 57) || // numbers
      (event.keyCode >= 96 && event.keyCode <= 105) || // numberpad
      (allowDecimals) || // period
      (event.keyCode === 8 || event.keyCode === 46) || // backspace / delete
      (event.keyCode >= 37 && event.keyCode <= 40) || // arrows
      (tabKey) || // tabbing
      (ctrlKey || metaKey) && !shiftKey); // misc;
  }

  onBlur(e) {
    if (this.options.inputPath) {
      this.appActions.setData('blur', this.options.inputPath);
    }
  }

  triggerValidation() {
    this.processValidation(this.currentValue);
  }

  processValidation(value) {
    const isValidObj = this.validation.validate(this.options.validationType, value || '');
    if (isValidObj !== undefined) {
      this.inputClass = this.inputClass.replace('error', '');
      this.isValid = isValidObj.state;
      if (!this.isValid) {
        this.options.msg = this.options.msg || {};
        this.options.msg.fail = isValidObj.msg;
        this.inputClass += ' error';
      } else {
        this.options.msg = undefined;
      }

      let isValid = this.isValid;
      if (this.showUp === false) {
        isValid = true;
      }
      this.validation.validCheck({
        id: this.options.attr.id,
        isValid
      });
      if (!isValid) {
        this.editField.nativeElement.focus();
      }
    }
  }
}
